import CheckRounded from "@mui/icons-material/CheckRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import {
  Box,
  Chip,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { useTranslate } from "react-admin";
import { Link as RouterLink } from "react-router-dom";

import {
  CrmDuplicateCandidateFieldsFragment,
  CrmDuplicateRecordFieldsFragment,
  CrmDuplicateStatus,
} from "../../generated/graphql";
import { CRM_COLORS, crmRecordPath, formatCrmDateTime } from "../../lib/crm";
import { color as tokenColor, sessionStatusStyle } from "../../theme/designTokens";
import { CrmObjectIcon } from "./CrmObjectIcon";

type Candidate = CrmDuplicateCandidateFieldsFragment;
type DuplicateRecord = CrmDuplicateRecordFieldsFragment;
type NormalizedAddress = DuplicateRecord["normalized"]["addresses"][number];
type Reason = Candidate["reasons"][number];
type ScoreSize = "medium" | "large";
type MatchState = "match" | "similar" | "conflict" | "missing";
type MatchResult = { state: MatchState; similarity?: number | null };

const KNOWN_REASON_CODES = [
  "name_exact",
  "name_fuzzy",
  "name_weak",
  "email_exact",
  "phone_exact",
  "address_exact",
  "address_fuzzy",
  "company_exact",
  "email_conflict",
  "phone_conflict",
  "extension_conflict",
  "house_conflict",
  "unit_conflict",
  "country_conflict",
] as const;
type ReasonCode = (typeof KNOWN_REASON_CODES)[number];
const REASON_CODES = new Set<string>(KNOWN_REASON_CODES);

const SCORE_SIZES: Record<ScoreSize, { diameter: number; fontSize: number }> = {
  medium: { diameter: 44, fontSize: 15 },
  large: { diameter: 64, fontSize: 22 },
};

const CONFLICT_STYLE = { color: "#B42318", bg: "#FDECEA", border: "#F5C2C0" };
const MATCH_ROW_TINT = "#EFF8F2";
const SIMILAR_ROW_TINT = "#FFF6E0";
const CONFLICT_ROW_TINT = "#FDEEEC";

const MATCH_STYLES: Record<MatchState, { color: string; bg: string; border: string; row: string }> = {
  match: { ...sessionStatusStyle.done, row: MATCH_ROW_TINT },
  similar: { ...sessionStatusStyle.pending, row: SIMILAR_ROW_TINT },
  conflict: { ...CONFLICT_STYLE, row: CONFLICT_ROW_TINT },
  missing: {
    color: tokenColor.gray,
    bg: tokenColor.background2,
    border: tokenColor.panelBorder,
    row: tokenColor.transparent,
  },
};

const SCORE_BAND_CRITICAL = { color: "#C62828", tint: "#FDECEA" };
const SCORE_BAND_HIGH = { color: "#C2410C", tint: "#FFEDD5" };
const SCORE_BAND_MEDIUM = { color: "#A16207", tint: "#FEF9C3" };
const SCORE_BAND_LOW = { color: sessionStatusStyle.running.color, tint: sessionStatusStyle.running.bg };

const STATUS_STYLES: Record<CrmDuplicateStatus, { color: string; bg: string; border: string }> = {
  [CrmDuplicateStatus.Pending]: sessionStatusStyle.running,
  [CrmDuplicateStatus.Deferred]: sessionStatusStyle.pending,
  [CrmDuplicateStatus.Confirmed]: sessionStatusStyle.done,
  [CrmDuplicateStatus.Dismissed]: {
    color: tokenColor.gray,
    bg: tokenColor.background2,
    border: tokenColor.panelBorder,
  },
};

const ADDRESS_FIELDS = ["street", "house", "unit", "postalCode", "city", "state", "country"] as const;
const ADDRESS_CONFLICT_CODES = [
  ["house_conflict", "house"],
  ["unit_conflict", "unit"],
  ["country_conflict", "country"],
] as const;

const visuallyHiddenSx = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
} as const;

export const scoreBand = (score: number) => {
  if (score >= 90) return SCORE_BAND_CRITICAL;
  if (score >= 75) return SCORE_BAND_HIGH;
  if (score >= 60) return SCORE_BAND_MEDIUM;
  return SCORE_BAND_LOW;
};

const compareValues = (a?: string | null, b?: string | null): MatchState =>
  !a || !b ? "missing" : a === b ? "match" : "conflict";

const findReason = (reasons: Reason[], ...codes: ReasonCode[]) =>
  reasons.find(reason => codes.some(code => code === reason.code));

const reasonState = (code: string): MatchState =>
  code.endsWith("_conflict")
    ? "conflict"
    : code.endsWith("_exact")
      ? "match"
      : code.endsWith("_fuzzy") || code.endsWith("_weak")
        ? "similar"
        : "missing";

const nameResult = ({ reasons, left, right }: Candidate): MatchResult => {
  if (findReason(reasons, "name_exact")) return { state: "match" };
  const similar = findReason(reasons, "name_fuzzy", "name_weak");
  if (similar) return { state: "similar", similarity: similar.similarity };
  return { state: compareValues(left.normalized.name, right.normalized.name) };
};

const companyResult = ({ reasons, left, right }: Candidate): MatchResult =>
  findReason(reasons, "company_exact")
    ? { state: "match" }
    : { state: compareValues(left.normalized.company, right.normalized.company) };

type AddressPair = { left: NormalizedAddress; right: NormalizedAddress; equal: number; conflictsAgree: boolean };

// The normalized address pair whose conflicts agree with the backend's conflict reasons, then the one sharing the
// most equal components; the first pair wins ties.
const bestAddressPair = (left: NormalizedAddress[], right: NormalizedAddress[], reasons: Reason[]) => {
  let best: AddressPair | null = null;
  for (const leftAddress of left) {
    for (const rightAddress of right) {
      const equal = ADDRESS_FIELDS.filter(
        field => leftAddress[field] && leftAddress[field] === rightAddress[field]
      ).length;
      const conflictsAgree = ADDRESS_CONFLICT_CODES.every(
        ([code, field]) =>
          !!findReason(reasons, code) === (compareValues(leftAddress[field], rightAddress[field]) === "conflict")
      );
      if (
        !best ||
        (conflictsAgree && !best.conflictsAgree) ||
        (conflictsAgree === best.conflictsAgree && equal > best.equal)
      )
        best = { left: leftAddress, right: rightAddress, equal, conflictsAgree };
    }
  }
  return best;
};

const addressResult = ({ reasons }: Candidate, pair: AddressPair | null): MatchResult => {
  if (findReason(reasons, "house_conflict", "unit_conflict", "country_conflict")) return { state: "conflict" };
  if (findReason(reasons, "address_exact")) return { state: "match" };
  const fuzzy = findReason(reasons, "address_fuzzy");
  if (fuzzy) return { state: "similar", similarity: fuzzy.similarity };
  const conflicting =
    !!pair && ADDRESS_FIELDS.some(field => compareValues(pair.left[field], pair.right[field]) === "conflict");
  return { state: conflicting ? "conflict" : "missing" };
};

const addressLines = (record: DuplicateRecord) =>
  record.addresses
    .map(address =>
      [address.street, [address.postalCode, address.city].filter(Boolean).join(" "), address.state, address.country]
        .filter(Boolean)
        .join("\n")
    )
    .filter(Boolean);

const MatchIcon = ({ state, size, className }: { state: MatchState; size: number; className?: string }) => {
  if (state === "similar")
    return (
      <Box
        component="span"
        aria-hidden
        className={className}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: size,
          height: size,
          fontSize: size,
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        ≈
      </Box>
    );
  const Icon = state === "match" ? CheckRounded : state === "conflict" ? CloseRounded : RemoveRounded;
  return <Icon className={className} sx={{ fontSize: size }} />;
};

export const CrmDuplicateScore = ({ score, size = "medium" }: { score: number; size?: ScoreSize }) => {
  const translate = useTranslate();
  const band = scoreBand(score);
  const { diameter, fontSize } = SCORE_SIZES[size];
  return (
    <Box
      role="img"
      aria-label={translate("crm.duplicates.scoreLabel", { score })}
      sx={{
        position: "relative",
        display: "inline-flex",
        flex: "none",
        alignItems: "center",
        justifyContent: "center",
        width: diameter,
        height: diameter,
        borderRadius: "50%",
        backgroundColor: band.tint,
      }}
    >
      <Box aria-hidden sx={{ position: "absolute", inset: 0, display: "flex" }}>
        <CircularProgress
          variant="determinate"
          value={score}
          size={diameter}
          thickness={4}
          sx={{ color: band.color }}
        />
      </Box>
      <Box component="span" sx={{ fontSize, fontWeight: 700, lineHeight: 1, color: band.color }}>
        {score}
      </Box>
    </Box>
  );
};

export const CrmDuplicateStatusChip = ({ status }: { status: CrmDuplicateStatus }) => {
  const translate = useTranslate();
  const style = STATUS_STYLES[status];
  return (
    <Chip
      size="small"
      variant="outlined"
      label={translate(`crm.duplicates.statuses.${status}`)}
      sx={{
        height: 22,
        fontSize: 12,
        fontWeight: 600,
        color: style.color,
        backgroundColor: style.bg,
        borderColor: style.border,
      }}
    />
  );
};

const MatchChip = ({ state, label }: { state: MatchState; label: string }) => {
  const style = MATCH_STYLES[state];
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<MatchIcon state={state} size={14} />}
      label={label}
      sx={{
        height: 22,
        fontSize: 12,
        fontWeight: 500,
        color: style.color,
        backgroundColor: style.bg,
        borderColor: style.border,
        "& .MuiChip-icon": { color: style.color },
      }}
    />
  );
};

export const CrmDuplicateReasonChips = ({ reasons }: { reasons: Reason[] }) => {
  const translate = useTranslate();
  const reasonText = (reason: Reason) => {
    const text = translate(`crm.duplicates.reasons.${REASON_CODES.has(reason.code) ? reason.code : "unknown"}`);
    return reason.similarity == null ? text : `${text} (${Math.round(reason.similarity * 100)} %)`;
  };
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
      {reasons.map((reason, index) => (
        <MatchChip key={index} state={reasonState(reason.code)} label={reasonText(reason)} />
      ))}
    </Box>
  );
};

const ValueLines = ({ lines, normalized }: { lines: string[]; normalized?: string | null }) => {
  const translate = useTranslate();
  return (
    <>
      {lines.length ? (
        lines.map((line, index) => (
          <Typography
            component="div"
            key={index}
            sx={{
              mb: index < lines.length - 1 ? 1 : 0,
              fontSize: 14,
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          >
            {line}
          </Typography>
        ))
      ) : (
        <Typography component="div" sx={{ fontSize: 14, color: CRM_COLORS.label }}>
          —
        </Typography>
      )}
      {normalized && normalized !== lines[0] && (
        <Typography
          component="div"
          sx={{ mt: 0.25, fontFamily: "monospace", fontSize: 12, color: CRM_COLORS.label, overflowWrap: "anywhere" }}
        >
          <span aria-hidden>≙ </span>
          <Box component="span" sx={visuallyHiddenSx}>
            {`${translate("crm.duplicates.normalizedValue")}: `}
          </Box>
          <span>{normalized}</span>
        </Typography>
      )}
    </>
  );
};

const MatchIndicator = ({ result }: { result: MatchResult }) => {
  const translate = useTranslate();
  const style = MATCH_STYLES[result.state];
  const solid = result.state !== "missing";
  const percent = result.state === "similar" && result.similarity != null ? Math.round(result.similarity * 100) : null;
  const text =
    percent == null
      ? translate(`crm.duplicates.matchStates.${result.state}`)
      : translate("crm.duplicates.matchStates.similarPercent", { percent });
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
      <Tooltip title={text}>
        <Box
          role="img"
          aria-label={text}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxSizing: "border-box",
            width: 28,
            height: 28,
            borderRadius: "50%",
            backgroundColor: solid ? style.color : style.bg,
            border: `1.5px solid ${solid ? style.color : style.border}`,
            color: solid ? tokenColor.white : style.color,
          }}
        >
          <MatchIcon state={result.state} size={18} />
        </Box>
      </Tooltip>
      {percent != null && (
        <Typography component="span" aria-hidden sx={{ fontSize: 11, fontWeight: 700, color: style.color }}>
          {percent} %
        </Typography>
      )}
    </Box>
  );
};

type ComparisonRowData = {
  key: string;
  label: string;
  caption?: string;
  indent?: boolean;
  left: ReactNode;
  right: ReactNode;
  result: MatchResult;
};

const ComparisonRow = ({ label, caption, indent, left, right, result }: Omit<ComparisonRowData, "key">) => {
  const style = MATCH_STYLES[result.state];
  return (
    <TableRow sx={{ backgroundColor: style.row }}>
      <TableCell
        component="th"
        scope="row"
        sx={{
          pl: indent ? 3 : 2,
          verticalAlign: "top",
          fontSize: indent ? 13 : 14,
          fontWeight: indent ? 400 : 600,
          color: indent ? CRM_COLORS.label : CRM_COLORS.text,
          boxShadow: result.state === "missing" ? undefined : `inset 3px 0 0 ${style.color}`,
        }}
      >
        {label}
        {caption && (
          <Typography component="div" sx={{ fontSize: 12, fontWeight: 400, color: CRM_COLORS.label }}>
            {caption}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>{left}</TableCell>
      <TableCell align="center" sx={{ verticalAlign: "top", px: 0.5 }}>
        <MatchIndicator result={result} />
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>{right}</TableCell>
    </TableRow>
  );
};

const RecordHeader = ({ record }: { record: DuplicateRecord }) => {
  const translate = useTranslate();
  const label = (key: string) => translate(`crm.duplicates.${key}`);
  const creationLabelId = `crm-duplicate-${record.object}-${record.salesforceId}`;
  const creation = [
    { key: "creator", caption: label("createdBy"), value: record.createdByName || "—" },
    { key: "date", caption: label("createdAt"), value: record.createdAt ? formatCrmDateTime(record.createdAt) : "—" },
  ];
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
      <CrmObjectIcon object={record.object} size="medium" />
      <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
        <Typography component="h3" sx={{ m: 0, fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}>
          <Link component={RouterLink} to={crmRecordPath(record.object, record.salesforceId)}>
            {record.name}
          </Link>
        </Typography>
        <Typography component="div" sx={{ fontSize: 12, fontWeight: 400, color: CRM_COLORS.label }}>
          {translate(`crm.objects.${record.object}.one`)}
        </Typography>
        <Box component="dl" sx={{ mt: 1, mb: 0 }}>
          {creation.map(({ key, caption, value }) => (
            <Box key={key} sx={{ display: "flex", flexWrap: "wrap", columnGap: 0.75 }}>
              <Typography
                component="dt"
                id={`${creationLabelId}-${key}`}
                sx={{ fontSize: 12, fontWeight: 400, color: CRM_COLORS.label }}
              >
                {caption}
              </Typography>
              <Typography
                component="dd"
                aria-labelledby={`${creationLabelId}-${key}`}
                sx={{ m: 0, fontSize: 13, fontWeight: 400, color: CRM_COLORS.text }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export const CrmDuplicateComparison = ({ candidate }: { candidate: Candidate }) => {
  const translate = useTranslate();
  const label = (key: string) => translate(`crm.duplicates.${key}`);
  const { left, right, reasons } = candidate;
  const single = (value?: string | null) => (value ? [value] : []);
  const showExtension = !!(left.normalized.phone?.extension || right.normalized.phone?.extension);
  const leftAddresses = left.normalized.addresses;
  const rightAddresses = right.normalized.addresses;
  const pair = bestAddressPair(leftAddresses, rightAddresses, reasons);
  const fuzzyAddress = findReason(reasons, "address_fuzzy");
  const rows: ComparisonRowData[] = [
    {
      key: "name",
      label: label("name"),
      left: <ValueLines lines={single(left.name)} normalized={left.normalized.name} />,
      right: <ValueLines lines={single(right.name)} normalized={right.normalized.name} />,
      result: nameResult(candidate),
    },
    {
      key: "company",
      label: label("company"),
      left: <ValueLines lines={single(left.company)} normalized={left.normalized.company} />,
      right: <ValueLines lines={single(right.company)} normalized={right.normalized.company} />,
      result: companyResult(candidate),
    },
    {
      key: "email",
      label: label("email"),
      left: <ValueLines lines={single(left.email)} normalized={left.normalized.email} />,
      right: <ValueLines lines={single(right.email)} normalized={right.normalized.email} />,
      result: { state: compareValues(left.normalized.email, right.normalized.email) },
    },
    {
      key: "phone",
      label: label("phone"),
      left: <ValueLines lines={single(left.phone)} normalized={left.normalized.phone?.base} />,
      right: <ValueLines lines={single(right.phone)} normalized={right.normalized.phone?.base} />,
      result: { state: compareValues(left.normalized.phone?.base, right.normalized.phone?.base) },
    },
    ...(showExtension
      ? [
          {
            key: "extension",
            label: label("extension"),
            left: <ValueLines lines={single(left.normalized.phone?.extension)} />,
            right: <ValueLines lines={single(right.normalized.phone?.extension)} />,
            result: { state: compareValues(left.normalized.phone?.extension, right.normalized.phone?.extension) },
          },
        ]
      : []),
    {
      key: "addresses",
      label: label("addresses"),
      caption: leftAddresses.length > 1 || rightAddresses.length > 1 ? label("bestAddressPair") : undefined,
      left: <ValueLines lines={addressLines(left)} />,
      right: <ValueLines lines={addressLines(right)} />,
      result: addressResult(candidate, pair),
    },
  ];
  const addressRows: ComparisonRowData[] = pair
    ? ADDRESS_FIELDS.filter(field => pair.left[field] || pair.right[field]).map(field => {
        const state = compareValues(pair.left[field], pair.right[field]);
        return {
          key: `address-${field}`,
          label: label(field),
          indent: true,
          left: <ValueLines lines={single(pair.left[field])} />,
          right: <ValueLines lines={single(pair.right[field])} />,
          result:
            field === "street" && state === "conflict" && fuzzyAddress
              ? { state: "similar", similarity: fuzzyAddress.similarity }
              : { state },
        };
      })
    : [];
  const counts = (["match", "similar", "conflict"] as const).map(state => ({
    state,
    count: rows.filter(row => row.result.state === state).length,
  }));
  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, pt: 2, mb: 2 }}>
        <CrmDuplicateScore score={candidate.score} size="large" />
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
          <CrmDuplicateStatusChip status={candidate.status} />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {counts
              .filter(({ count }) => count > 0)
              .map(({ state, count }) => (
                <MatchChip key={state} state={state} label={translate(`crm.duplicates.summary.${state}`, { count })} />
              ))}
          </Box>
        </Box>
      </Box>
      <Table
        stickyHeader
        size="small"
        aria-label={label("comparison")}
        sx={{ tableLayout: "fixed", "& .MuiTableCell-root": { borderBottom: `1px solid ${tokenColor.panelBorder}` } }}
      >
        <colgroup>
          <Box component="col" sx={{ width: { xs: 96, sm: 150 } }} />
          <col />
          <Box component="col" sx={{ width: 64 }} />
          <col />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell sx={{ backgroundColor: CRM_COLORS.tableHeader }}>
              <Box component="span" sx={visuallyHiddenSx}>
                {label("field")}
              </Box>
            </TableCell>
            <TableCell align="left" sx={{ verticalAlign: "top", backgroundColor: CRM_COLORS.tableHeader }}>
              <RecordHeader record={left} />
            </TableCell>
            <TableCell sx={{ backgroundColor: CRM_COLORS.tableHeader }}>
              <Box component="span" sx={visuallyHiddenSx}>
                {label("matchColumn")}
              </Box>
            </TableCell>
            <TableCell align="left" sx={{ verticalAlign: "top", backgroundColor: CRM_COLORS.tableHeader }}>
              <RecordHeader record={right} />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...rows, ...addressRows].map(({ key, ...row }) => (
            <ComparisonRow key={key} {...row} />
          ))}
        </TableBody>
      </Table>
    </>
  );
};
